import * as SVG from 'svg.js';

import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges,
         OnDestroy, OnInit, Output, ViewChild
       } from '@angular/core';

import { injectMultiversionImageBehavior
       } from '../multiversion-image/multiversion-image';
import { CoordinateTranslation } from './coordinate-translation';
import { CropHandle } from './crop-handle';
import { pinSVGElement, Region, RegionLike } from './region';

class NumberSequenceChangeDetector
{
  private values: number[] = undefined;

  public swapCompare(newValues: number[]): boolean
  {
    const oldValues = this.values;
    this.values = newValues;

    if (oldValues === undefined || newValues === undefined) {
      return true;
    }

    if (oldValues.length !== newValues.length) {
      return true;
    }

    if (oldValues
            .map((old, idx) => old === newValues[idx])
            .some((b) => b === false))
    {
      return true;
    }

    return false; // unchanged
  }
}

@Component({
  selector: 'image-cropper',
  template: '<div #svgContainer class=\'image-cropper-svg-container\'></div>',
  styleUrls: ['./image-cropper.component.scss'],
})
export class ImageCropperComponent implements OnInit, OnChanges, OnDestroy
{
  private static readonly MAX_IMAGE_CROPPER_HEIGHT: number = 400;
  private static readonly MIN_CONTAINER_HEIGHT: number = 140;

  @ViewChild('svgContainer')
  private container: ElementRef;

  @Input() public imageUrl: string = '';
  @Input() public maskPath: string = 'M 0 0 h 1 v 1 h -1 v -1';
  @Input() public availableHeight: number;

  @Input() public editableArea: any = undefined;

  private maxHeight: number;
  private _targetRegion: Region = undefined;
  private _selectedRegion: Region = Region.empty();

  @Output() public selectedRegionChange: EventEmitter<RegionLike> =
      new EventEmitter<RegionLike>();

  @Output() public onImageLoaded: EventEmitter<SVG.Image> =
      new EventEmitter<SVG.Image>();

  private svgDoc: SVG.Doc = undefined;
  private translation: CoordinateTranslation;

  private backgroundImage: SVG.Image;
  private imageInfo: any = undefined;

  private cutOutShape: SVG.Path;
  private cropHandle: CropHandle;

  private boundsConfig: NumberSequenceChangeDetector =
      new NumberSequenceChangeDetector();

  public ngOnInit(): void
  {
    const nativeContainer = this.container.nativeElement;
    nativeContainer.ondragstart = () => false;
    this.svgDoc = SVG(nativeContainer);
    this.respondToContainerAttributeChange(nativeContainer);

    this.backgroundImage =
        injectMultiversionImageBehavior(this.svgDoc.image());

    this.backgroundImage.loaded((info) => {
      this.imageInfo = info;
      this.updateMaxHeight();
      this.tryFitImage();
      this.onResize(undefined);
      this.backgroundImage.load();
      this.onImageLoaded.emit(this.backgroundImage);
    });

    this.cutOutShape = this.svgDoc.path();

    this.cropHandle = new CropHandle(
        this.svgDoc,
        this.cutOutShape,
        this.onCropHandleSelect.bind(this));

    this.changeBackgroundImage();
    this.changeMaskPath();
    this.changeSelectedRegion();

    window.addEventListener(
        'orientationchange', this.orientationChange.bind(this));
  }

  public ngOnDestroy(): void
  {
    this.cropHandle.destructor();
    window.removeEventListener(
        'orientationchange', this.orientationChange.bind(this));
  }

  public ngOnChanges(changes): void
  {
    const reactToChange = (propertyName, resolveFn) => {
      const change = changes[propertyName];

      if (change !== undefined && !change.isFirstChange()) {
        resolveFn();
      }
    };

    reactToChange('availableHeight', this.updateMaxHeight.bind(this));
    reactToChange('imageUrl', this.changeBackgroundImage.bind(this));
    reactToChange('image', this.changeBackgroundImage.bind(this));
    reactToChange('selectedRegion', this.changeSelectedRegion.bind(this));
    reactToChange('maskPath', this.changeMaskPath.bind(this));
    reactToChange('editableArea', this.onResize.bind(this));
  }

  private orientationChange(event: Event): void
  {
    this.tryFitImage();
    this.onResize(undefined);
  }

  public get svgDocument(): SVG.Doc
  {
    return this.svgDoc;
  }

  private updateMaxHeight(): void
  {
    this.maxHeight = Math.min(
        Math.max(
            this.availableHeight ? this.availableHeight : Infinity,
            ImageCropperComponent.MIN_CONTAINER_HEIGHT),
        ImageCropperComponent.MAX_IMAGE_CROPPER_HEIGHT);
  }

  private changeBackgroundImage(): void
  {
    this.backgroundImage.load(this.imageUrl);
  }

  private changeSelectedRegion(): void
  {
    this.onResize(undefined, true);
  }

  private changeMaskPath(): void
  {
    this.cutOutShape.plot(this.maskPath);
  }

  @Input()
  public set targetRegion(value: RegionLike)
  {
    this._targetRegion = Region.fromSimilar(value);
  }

  @Input()
  public set image(image: HTMLImageElement)
  {
    if (image !== undefined) {
      this.imageUrl = image.src;
    }
  }

  @Input()
  public set selectedRegion(jsonDescription)
  {
    this._selectedRegion = Region.fromSimilar(jsonDescription);
  }

  public get selectedRegion(): RegionLike
  {
    return this._selectedRegion;
  }

  private tryFitImage(): void
  {
    const divContainer = this.container.nativeElement as HTMLDivElement;

    const aspectRatio = this.imageInfo.height / this.imageInfo.width;

    const desiredHeight = Math.min(
        aspectRatio * divContainer.getBoundingClientRect().width,
        (window.innerHeight - ImageCropperComponent.MIN_CONTAINER_HEIGHT),
        this.maxHeight);

    divContainer.style.height = `${desiredHeight}px`;
  }

  @HostListener('window:resize') public onResize(ev, force=false): void
  {
    if (this.imageInfo !== undefined) {
      const target =
          this._targetRegion || Region.fromImageInfo(this.imageInfo);

      const viewport = this.viewportRegion;

      if (force || this.boundsConfig.swapCompare(
              [target.width, target.height, viewport.width, viewport.height]))
      {
        this.reconfigure(target, viewport);
      }
    }
  }

  protected onCropHandleSelect(selection: Region): void
  {
    this._selectedRegion = selection;
    this.selectedRegionChange.emit(selection);
  }

  private get viewportRegion(): Region
  {
    return Region.fromRBox(this.svgDoc.rbox());
  }

  private reconfigure(target: Region, viewport: Region): void
  {
    this.translation = new CoordinateTranslation(target, viewport);
    this.fitBackground();

    const selectedRegion = this._selectedRegion;

    this.cropHandle.reconfigure(
        Region.fromBBox(this.backgroundImage.bbox()),
        this.translation,
        selectedRegion,
        this.editableArea);
  }

  private fitBackground(): void
  {
    pinSVGElement(this.backgroundImage, this.translation.legalRegion);
  }

  // If the div's height changes on demand dynamically (like on iPhones),
  // this method triggers a further resize after that height change
  private respondToContainerAttributeChange(container): void
  {
    let observer = new MutationObserver(mutations => {
      this.onResize(undefined);
    });
    observer.observe(container, {attributes: true});
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/image-cropper/image-cropper.component.ts